/**
 * useChangePassword Hook
 *
 * Changes existing password using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const ChangePasswordDocument = `
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseChangePasswordResult {
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Change existing password.
 */
export function useChangePassword(): UseChangePasswordResult {
  const [result, executeMutation] = useMutation<
    { changePassword: boolean },
    { currentPassword: string; newPassword: string }
  >(ChangePasswordDocument);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<boolean> => {
      try {
        const response = await executeMutation({ currentPassword, newPassword });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to change password"
          );
        }

        errorTracking.addBreadcrumb({
          category: "profile",
          message: "Password changed",
        });

        return response.data?.changePassword ?? true;
      } catch (err) {
        const passwordError =
          err instanceof Error ? err : new Error("Failed to change password");

        errorTracking.captureError(passwordError, {
          tags: { feature: "profile", action: "changePassword" },
        });

        throw passwordError;
      }
    },
    [executeMutation]
  );

  return {
    changePassword,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
